import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Microscope, Sparkles, FlaskConical, Users } from "lucide-react";

export default function WarumLacLive() {
  return (
    <div className="grain-bg">
      <Seo
        title="Warum mir das Thema Laktoseintoleranz am Herzen liegt"
        description="Der persönliche Weg hinter LacLive: vom strikten Verzicht zur wissenschaftlich fundierten Strategie. Warum Metaanalysen zum Darmmikrobiom mich überzeugt haben."
        keywords="LacLive Autor, Motivation, Laktoseintoleranz Erfahrung, Mikrobiom Ansatz"
        path="/warum-laclive"
      />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10 lg:pt-24">
        <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Der Weg hinter LacLive
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
          Warum mir das Thema Laktoseintoleranz am Herzen liegt
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-3xl leading-relaxed">
          Wissenschaft ist mein Beruf - auch wenn ich nicht direkt im Bereich Ernährung forsche. Aber ohne die richtige Ernährung ist kein Gesundheitskonzept vollständig, und das Thema Laktoseintoleranz betrifft mich persönlich. Es hat mich lange beschäftigt, bis ich verstanden habe, woher meine Symptome kamen. Wie sehr viele andere habe auch ich die Laktoseunverträglichkeit erst im Erwachsenenalter entwickelt - ein schleichender Prozess, der oft mit typischen Verdauungsbeschwerden wie Blähungen, Bauchschmerzen, Krämpfen und manchmal sogar Durchfall einherging.
        </p>
      </section>

      {/* Story blocks */}
      <section className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <Card className="p-8 rounded-2xl border-slate-100 card-lift">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0 mt-1">
              <Users className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900 text-xl tracking-tight">Meine persönliche Erfahrung</h2>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Anfangs bin ich - wie viele Betroffene - die klassische Vermeidungsstrategie gefahren: strikter Verzicht auf laktosehaltige Lebensmittel. Das brachte zwar kurzfristig Erleichterung, führte aber auch zu erheblichen Einschränkungen im Alltag: weniger Auswahl beim Essen, höhere Kosten beim Einkauf und die ständige Sorge, unbeabsichtigt doch Laktose zu konsumieren.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-8 rounded-2xl border-slate-100 card-lift">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0 mt-1">
              <Microscope className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900 text-xl tracking-tight">Der wissenschaftliche Ansatz</h2>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Doch dann kam der Gedanke: Wenn es Bakterien im Darm gibt, die mit Laktose nichts anfangen können, dann muss es doch auch das Gegenteil geben - also Mikroorganismen, die Laktose sehr wohl verwerten können. Metaanalysen zu wissenschaftlichen Fragestellungen beherrsche ich aus meinem Berufsalltag sehr gut - warum also nicht auch zu diesem Thema?
              </p>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Tatsächlich fand ich mehrere Studien, die genau das untersucht haben: die Rolle des Darmmikrobioms bei Laktoseintoleranz. Die Forschung zeigt, dass bestimmte Bakterienstämme wie <em>Bifidobacterium</em> Laktose metabolisieren können und so die Symptome lindern. Andere Studien identifizierten schützende Bakterienfamilien wie <em>Veillonellaceae</em> sowie riskante Stämme wie <em>Anaerotruncus</em> oder <em>Ruminococcus2</em>.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-8 rounded-2xl border-slate-100 card-lift">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0 mt-1">
              <FlaskConical className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900 text-xl tracking-tight">Vom Wissen zur praktischen Strategie</h2>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Aus diesen Erkenntnissen entstand eine Strategie, die auf wissenschaftlicher Evidenz basiert. Bei mir hat das wunderbar funktioniert - und auch bei anderen Patienten, die diesen Ansatz ausprobiert haben.
              </p>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Das hier auf laclive.de veröffentlichte Protokoll ist die Quintessenz vieler Studien, gepackt in ein simples, alltagstaugliches Verfahren. Mein Ziel ist es, dass diese evidenzbasierte Strategie auch vielen anderen Menschen helfen kann, die - wie ich damals - nach einer praktikablen Lösung suchen, die über bloßen Verzicht hinausgeht.
              </p>
              <p className="mt-4 text-slate-900 font-medium">
                Denn Wissenschaft soll nicht nur in Fachzeitschriften stehen - sie soll im echten Leben wirken.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-slate-100 p-8 lg:p-12 text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900">Bereit für den evidenzbasierten Weg?</h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Der 12-Wochen-Plan macht aus dieser Strategie ein alltagstaugliches Protokoll - mit Rezepten, Mengen und wöchentlicher Steigerung.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/paywall">
              <Button className="rounded-full h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white btn-lift" data-testid="warum-cta-paywall">
                12-Wochen-Plan freischalten <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/wissenschaft">
              <Button variant="outline" className="rounded-full h-12 px-6 btn-lift" data-testid="warum-cta-wissenschaft">
                Studienlage ansehen
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
